import {
  Box,
  Button,
  Center,
  HStack,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
  useBreakpoint,
  useDisclosure,
} from "@chakra-ui/react";
import NotificationsPopupList from "@components/modules/notifications/listing/Popup";
import { MenuItemLink } from "@components/ui-kit";
import { Trans } from "@lingui/macro";
import { PROPOSALS_RETURM_PARAMS } from "@src/components/modules/budgets/export";
import { CreateBudgetMenu } from "@src/components/modules/budgets/export/CreateBudgetMenu";
import { TaskNotificationsPopupList } from "@src/components/modules/notifications/task-notifications";
import { CreateProjectMenu } from "@src/components/modules/projects/create/project-menu";
import { Icon } from "@src/components/ui-kit/Icon";
import { Can, can } from "@src/utils/components/permissions";
import { useStore } from "@src/utils/hooks";
import { ProjectSearch } from "../ProjectSearch";
import { TimeTrackingPopover } from "../TimeTrackingPopover";
import { LoggedUser } from "./LoggedUser";

type NavigationWidgetsProps = {
  appSubNavChildCount: number;
};
export const NavigationWidgets = ({
  appSubNavChildCount,
}: NavigationWidgetsProps) => {
  const newButtonDisclosure = useDisclosure();
  const {
    taskFormModalStore,
    taskDetailModalStore,
    allocationModalStore,
    authStore,
  } = useStore();
  const breakpoint = useBreakpoint();

  const handleNewTask = () => {
    taskFormModalStore.modalState.onOpen({
      onSubmit: (task) => {
        taskDetailModalStore.disclosureState.onOpen({
          id: task.id,
        });
      },
    });
  };

  return (
    <HStack spacing="0">
      <ProjectSearch />
      {can("task_create_all", "task_create_scoped") &&
        (breakpoint !== "base" || appSubNavChildCount === 0) &&
        (authStore.isInternalUser ? (
          <Menu
            isOpen={newButtonDisclosure.isOpen}
            onClose={newButtonDisclosure.onClose}
            onOpen={newButtonDisclosure.onOpen}
          >
            <MenuButton
              as={Button}
              mx="2!"
              px={{
                base: "2",
                lg: "4",
              }}
              colorScheme="violet"
              leftIcon={<Icon name="plus" />}
            >
              <Trans>New</Trans>
            </MenuButton>
            <MenuList
              sx={{
                "&:focus:not([data-focus-visible-added]):not([data-focus-visible-disabled])":
                  {
                    shadow: "lg", // default shadow
                  },
              }}
              shadow="lg"
            >
              <Can do={["project_create_all", "project_create_team"]}>
                <MenuItem
                  as={CreateBudgetMenu}
                  listPlacement="left-start"
                  menuButtonProps={{
                    w: "full",
                    _hover: {
                      background: "gray.100",
                    },
                  }}
                  menuPlacement="left-start"
                  onItemClick={newButtonDisclosure.onClose}
                  queryParams={{
                    returnParams: PROPOSALS_RETURM_PARAMS,
                  }}
                >
                  <HStack px="3" py="1.5" spacing="2">
                    <Icon name="file-plus-02" />
                    <Text fontWeight="medium">
                      <Trans>Budget</Trans>
                    </Text>
                  </HStack>
                </MenuItem>
                <MenuDivider />
              </Can>
              <Can
                do={[
                  "project_create_all",
                  "project_create_team",
                  "project_create_own",
                ]}
              >
                <MenuItem
                  as={CreateProjectMenu}
                  listPlacement="left-start"
                  menuButtonProps={{
                    w: "full",
                    _hover: {
                      background: "gray.100",
                    },
                  }}
                  menuPlacement="left-start"
                  onItemSelect={newButtonDisclosure.onClose}
                >
                  <HStack px="3" py="1.5" spacing="2">
                    <Icon name="folder" />
                    <Trans>Project</Trans>
                  </HStack>
                </MenuItem>
              </Can>
              <Can do={["task_create_all", "task_create_scoped"]}>
                <MenuItem
                  icon={<Icon name="clipboard-check" />}
                  onClick={handleNewTask}
                >
                  <Text fontWeight="medium">
                    <Trans>Task</Trans>
                  </Text>
                </MenuItem>
              </Can>
              <MenuItem
                icon={<Icon name="calendar-plus-02" />}
                onClick={() => {
                  let options: { userId: string; date: Date } | undefined;

                  if (authStore.user?.id) {
                    options = {
                      userId: authStore.user.id,
                      date: new Date(),
                    };
                  } else {
                    options = undefined;
                  }

                  allocationModalStore.modalState.onOpen(options);
                }}
              >
                <Text fontWeight="medium">
                  <Trans>Capacity allocation</Trans>
                </Text>
              </MenuItem>
              <Can do="expense_create">
                <MenuDivider />
                <MenuItemLink
                  icon={<Icon name="wallet-02" />}
                  href={{
                    pathname: "/expenses/create",
                  }}
                >
                  <Text fontWeight="medium">
                    <Trans>Expense</Trans>
                  </Text>
                </MenuItemLink>
              </Can>
              <Can
                do={[
                  "requestForInvoicing_create_all",
                  "requestForInvoicing_create_team",
                ]}
              >
                <MenuDivider />
                <MenuItemLink
                  href={{
                    pathname: "/invoices/for-invoicing/create-invoice",
                  }}
                  icon={
                    <Box pos="relative">
                      <Icon name="file-01" />
                      <Center
                        pos="absolute"
                        top="0"
                        right="0"
                        bottom="0"
                        left="0"
                      >
                        <Icon name="currency-dollar" w="2" h="2" />
                      </Center>
                    </Box>
                  }
                >
                  <Text fontWeight="medium">
                    <Trans>Invoice</Trans>
                  </Text>
                </MenuItemLink>
                <MenuItemLink
                  href={{
                    pathname: "/invoices/for-invoicing/create-proforma",
                  }}
                  icon={<Icon name="file-question-01" />}
                >
                  <Text fontWeight="medium">
                    <Trans>Proforma</Trans>
                  </Text>
                </MenuItemLink>
                <MenuItemLink
                  icon={<Icon name="file-minus-01" />}
                  href={{
                    pathname: "/invoices/for-invoicing/create-credit-note",
                  }}
                >
                  <Text fontWeight="medium">
                    <Trans>Credit note</Trans>
                  </Text>
                </MenuItemLink>
              </Can>
            </MenuList>
          </Menu>
        ) : (
          <Button
            mx="2!"
            px={{
              base: "2",
              lg: "4",
            }}
            colorScheme="violet"
            leftIcon={<Icon name="plus" />}
            onClick={handleNewTask}
          >
            <Trans>Task</Trans>
          </Button>
        ))}
      <Can
        do={[
          "timeTracking_timer_use_own",
          "timeTracking_timer_use_all",
          "timeTracking_timer_use_team",
        ]}
      >
        <TimeTrackingPopover />
      </Can>
      <NotificationsPopupList />
      <TaskNotificationsPopupList />
      {authStore.user && <LoggedUser user={authStore.user} />}
    </HStack>
  );
};
